import * as DateFns from 'date-fns';

export function dateFormat(date: Date, formatString: string) {
  return DateFns.format(date, formatString);
}

export function isSameDate(date1: Date, date2: Date) {
  return DateFns.isSameDay(date1, date2);
}

export function isSameYearAndMonth(date1: Date, date2: Date) {
  return DateFns.isSameYear(date1, date2) && DateFns.isSameMonth(date1, date2);
}

export function isDateSameWeek(date1: Date, date2: Date) {
  return DateFns.isSameWeek(date1, date2);
}

export interface DateDurationOptions {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export function addDate(date: Date, duration: DateDurationOptions) {
  return DateFns.add(date, duration);
}

export function subDate(date: Date, duration: DateDurationOptions) {
  return DateFns.sub(date, duration);
}

export function parseDate(dateString: string, formatString: string) {
  return DateFns.parse(dateString, formatString, new Date());
}

export function parseDateISO(dateString: string) {
  return DateFns.parseISO(dateString);
}

export function formatDateISO(date: Date) {
  return DateFns.formatISO(date);
}

export function intervalDuration(start: Date, end: Date) {
  return DateFns.intervalToDuration({start, end});
}

export function monthOfInterval(start: Date, end: Date) {
  return DateFns.eachMonthOfInterval({start, end});
}

export function dayOfInterval(start: Date, end: Date) {
  return DateFns.eachDayOfInterval({start, end});
}

export function weekOfInterval(start: Date, end: Date) {
  return DateFns.eachWeekOfInterval({start, end});
}

export function startDateOfDay(date: Date) {
  return DateFns.startOfDay(date);
}

export function startDateOfWeek(date: Date) {
  return DateFns.startOfWeek(date);
}

export function lastDateOfWeek(date: Date) {
  return DateFns.lastDayOfWeek(date);
}

export function getWeekIndexOfMonth(date: Date) {
  return DateFns.getWeekOfMonth(date);
}

export function startDateOfMonth(date: Date) {
  return DateFns.startOfMonth(date);
}

export function endDateOfMonth(date: Date) {
  return DateFns.endOfMonth(date);
}

export function differenceInCalendarWeek(date1: Date, date2: Date) {
  return DateFns.differenceInCalendarWeeks(date1, date2);
}

interface DifferenceInDayOptions {
  includeReferenceDate?: boolean;
}

export function differenceInDay(date1: Date, date2: Date, differenceInDayOptions?: DifferenceInDayOptions) {
  const differenceDays = DateFns.differenceInDays(DateFns.startOfDay(date1), DateFns.startOfDay(date2));
  return differenceInDayOptions?.includeReferenceDate ? differenceDays + 1 : differenceDays;
}

export function isDateFormat(date: Date) {
  return DateFns.isDate(date);
}

export function setDay(date: Date, dayOfMonth: number) {
  return DateFns.setDate(date, dayOfMonth);
}

export function parseDateWithFormat(date: Date | string | undefined | null) {
  if (!date) {
    return null;
  }
  if (typeof date === 'string') {
    const dateString = date.replace(/[-:T+]/g, '').slice(0, 14);
    switch (dateString.length) {
      case 6:
        return DateFns.parse(dateString, 'yyyyMM', new Date());
      case 8:
        return DateFns.parse(dateString, 'yyyyMMdd', new Date());
      case 12:
        return DateFns.parse(dateString, 'yyyyMMddHHmm', new Date());
      case 14:
        return DateFns.parse(dateString, 'yyyyMMddHHmmss', new Date());
      default:
        const parsedDate = DateFns.parseISO(date);
        return DateFns.isValid(parsedDate) ? parsedDate : null;
    }
  }
  return date;
}

export function isValidDate(dateString: string | null | undefined) {
  if (!dateString) {
    return false;
  }
  const [year, month, day] = dateString.split('-').map((v) => parseInt(v, 10));

  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * 타임존 오프셋을 UTC 문자열 형식으로 변환 (예: "UTC+1", "UTC+9", "UTC-5:30")
 * "+09:00" 형식의 타임존 오프셋을 "UTC+9" 형식으로 변환합니다.
 * 분이 0이 아닌 경우 (예: "+05:30"), 분을 포함하여 출력합니다 (예: "UTC+5:30")
 *
 * @param timezoneOffset - 타임존 오프셋 문자열 (예: "+09:00", "-05:30")
 * @returns 포맷된 타임존 문자열 (예: "UTC+9", "UTC-5:30")
 */
export function formatTimezoneToUTC(timezoneOffset: string): string {
  if (!timezoneOffset) {
    return '';
  }
  // 오프셋 문자열 파싱 (예: "+09:00" -> hours: 9, minutes: 0)
  const match = timezoneOffset.match(/([+-])(\d{2}):(\d{2})/);

  if (!match) {
    return 'UTC';
  }

  const [, sign, hoursStr, minutesStr] = match;
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);

  // 분이 0이면 "UTC+9", 분이 있으면 "UTC+9:30" 형식으로 반환
  if (minutes === 0) {
    return `UTC${sign}${hours}`;
  }
  return `UTC${sign}${hours}:${minutesStr}`;
}
